import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { Client } from '../models/client.models';
import { ApiCuentaCorrienteResponse } from '../models/cuenta-corriente.models';

@Injectable({
  providedIn: 'root'
})
export class ClientesService {

  negocio: Observable<Client | any>;
  clientes: Observable<Client[] | any>;

  constructor(private http: HttpClient) { }

  getClientes(pagina: number, itemXPag: number, search?: string, type?: string, ): Observable<Client | any> {
    return this.clientes = this.http.get(environment.apiUrl + `client/list?pag=${pagina}&itemXPag=${itemXPag}&search=${search}&type=${type}`);
  }

  getClientesBytype(type: string): Observable<Client | any> {
    return this.clientes = this.http.get(environment.apiUrl + 'client/type/' + type);
  }

  getNuevoNumeroCliente(): Observable<any> {
    return this.http.get(environment.apiUrl + 'client/code/new');
  }

  getCantidadClientesActivos(): Observable<Client | any> {
      return this.clientes = this.http.get(environment.apiUrl + 'clientes/cantidad');
  }

  getCliente(cuit: string): Observable<Client | any> {
      return this.clientes = this.http.get(environment.apiUrl + 'client/' + cuit);
  }

  getClienteById(id: number): Observable<Client | any> {
      return this.http.get(environment.apiUrl + 'client/' + id);
  }

  /**
   * Obtiene los indicadores financieros de un cliente
   * @param id Identificador único del cliente
   * @returns Observable con los indicadores del cliente (saldo, deuda, comprobantes, etc)
   */
  getClienteIndicador(id: number): Observable<{
    clientId: number, 
    balance: number, 
    overdueAmount: number, 
    voucherCount: number,
    totalBilled: number
  } | any> {
      if (!id || isNaN(id)) {
        throw new Error('ID de cliente inválido');
      }
      
      return this.http.get<{
        clientId: number, 
        balance: number, 
        overdueAmount: number, 
        voucherCount: number,
        totalBilled: number
      }>(environment.apiUrl + `client/${id}/indicadores`);
  }

  getExisteClienteByCuit(nro: string): Observable<Client | any> {
      return this.clientes = this.http.get(environment.apiUrl + 'client/existe/' + nro);
  }

  getClienteByNumero(nro: string): Observable<Client | any> {
      return this.clientes = this.http.get(environment.apiUrl + 'client/numero/' + nro);
  }

  getClienteByRazonSocial(razonsocial: string): Observable<Client | any> {
      return this.clientes = this.http.get(environment.apiUrl + 'client/razonsocial/' + razonsocial);
  }


  eliminarCliente(id: number): Observable<string | any> {
      return this.http.delete(environment.apiUrl + 'client/' + id);
  }

  actualizarCliente(id: number, cliente: Client): Observable<string | any> {
      return this.http.patch(environment.apiUrl + 'client/' + id, cliente);
  }

  guardarCliente(cliente: Client): Observable<any> {
      return this.http.post(environment.apiUrl + 'client', cliente);
  }

  /**
   * Establece el saldo inicial para un cliente
   * @param clientId Identificador único del cliente
   * @param amount Monto del saldo inicial
   * @param observations Observaciones sobre el saldo inicial
   * @returns Observable con la respuesta del servidor
   */
  establecerSaldoInicial(customerId: number, amount: number, type: string, description: string): Observable<{success: boolean, message: string}> {
      if (!customerId || isNaN(customerId)) {
        throw new Error('ID de cliente inválido');
      }
      
      if (isNaN(amount)) {
        throw new Error('Monto inválido');
      }
      
      return this.http.post<{success: boolean, message: string}>(environment.apiUrl + 'customer-current-account-movements', {
        customerId, 
        amount, 
        type,
        description
      });
  }

  /**
   * Obtiene los movimientos de cuenta corriente de un cliente
   * @param clienteId Identificador único del cliente
   * @returns Observable con los movimientos de cuenta corriente
   */
  getMovimientosCuentaCorriente(clienteId: number): Observable<ApiCuentaCorrienteResponse> {
      if (!clienteId || isNaN(clienteId)) {
        throw new Error('ID de cliente inválido');
      }
      
      return this.http.get<ApiCuentaCorrienteResponse>(environment.apiUrl + `client/${clienteId}/account-summary`);
  }

  /**
   * Obtiene los últimos movimientos de cuenta corriente de un cliente con filtros de fecha
   * @param customerId Identificador único del cliente
   * @param startDate Fecha de inicio (formato ISO o Date)
   * @param endDate Fecha de fin (formato ISO o Date)
   * @returns Observable con los movimientos de cuenta corriente filtrados
   */
  getCustomerCurrentAccountMovements(
    customerId: number, 
    startDate?: string | Date, 
    endDate?: string | Date
  ): Observable<ApiCuentaCorrienteResponse> {
    if (!customerId || isNaN(customerId)) {
      throw new Error('ID de cliente inválido');
    }

    // Construir parámetros de consulta usando HttpParams
    let params = new HttpParams();
    params = params.set('customerId', customerId.toString());

    if (startDate) {
      const startDateString = startDate instanceof Date ? startDate.toISOString().split('T')[0] : startDate;
      params = params.set('startDate', startDateString);
    }

    if (endDate) {
      const endDateString = endDate instanceof Date ? endDate.toISOString().split('T')[0] : endDate;
      params = params.set('endDate', endDateString);
    }

    return this.http.get<ApiCuentaCorrienteResponse>(
      `${environment.apiUrl}customer-current-account-movements`,
      { params }
    );
  }

}
